/// "Playing now" presence via Firebase Realtime Database.
///
/// RTDB's `onDisconnect` is the canonical pattern: the server
/// detects when a client's WebSocket drops (tab closed, browser
/// crashed, network died) and runs the registered cleanup write
/// instantly. No heartbeats; no per-write Firestore cost.
///
/// Layout:
///   /presence/{uid} = {
///     online: true,
///     lastSeen: serverTimestamp,
///     levelId?: "VNLK"          // present only while the user is
///                               // inside a level (GameView mounted);
///                               // absent while on the home screen
///   }
///
/// Lifecycle:
///   - On mount: subscribe to `.info/connected` (RTDB built-in).
///   - On connect: register `onDisconnect → remove`, then write
///     ourselves online. The order matters: register the disconnect
///     handler BEFORE the online write, so a flaky connection that
///     drops between the two doesn't leave a ghost online entry.
///   - On level enter/exit: call `setActivePresenceLevel(uid, id|null)`.
///     This patches just the `levelId` field — the rest of the
///     entry and the disconnect handler stay intact.
///   - On unmount: unsubscribe; the natural disconnect cleanup
///     fires server-side.

import {
  onDisconnect,
  onValue,
  ref,
  remove,
  serverTimestamp,
  set,
  update,
} from 'firebase/database'
import { firebaseDatabase } from '../firebase/app'

const PRESENCE_PATH = 'presence'

/// How often the client refreshes `lastSeen` while online. The
/// server-side `cleanupStalePresence` Cloud Function sweeps any
/// entry whose `lastSeen` is older than ~5 minutes, so the
/// heartbeat must be comfortably shorter than that threshold. Two
/// minutes gives roughly 2x headroom for a missed beat.
///
/// Why we need this at all: RTDB's `onDisconnect` only fires on a
/// clean WebSocket close. Real-world disconnects often don't
/// produce one — laptop lid closed, mobile tab suspended, browser
/// crashed, Wi-Fi dropped, OS-level tab discard. Without a
/// heartbeat the server-side cleanup can't distinguish "actively
/// idle on home screen" from "ghost", because `lastSeen` would
/// only get written at connect / level-change time.
const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000

/// Mark `uid` as online. Returns an unsubscribe function that the
/// caller should invoke on teardown (e.g., sign-out). Note: in the
/// happy "user closes the tab" path we never get a chance to call
/// the unsubscribe — that's exactly what `onDisconnect` is for.
export function startPresence(uid: string): () => void {
  const userRef = ref(firebaseDatabase, `${PRESENCE_PATH}/${uid}`)
  const connectedRef = ref(firebaseDatabase, '.info/connected')

  const unsubConnected = onValue(connectedRef, (snap) => {
    if (snap.val() !== true) return
    // Register cleanup first — order matters (see file header).
    onDisconnect(userRef)
      .remove()
      .then(() => {
        return set(userRef, {
          online: true,
          lastSeen: serverTimestamp(),
        })
      })
      .catch((err) => {
        console.warn('presence write failed', err)
      })
  })

  // Heartbeat. Refresh `lastSeen` periodically so the server-side
  // cleanup function can tell active users from ghosts. We don't
  // touch `levelId` here — `setActivePresenceLevel` owns that.
  // Backgrounded tabs get throttled to ~once per minute by
  // modern browsers, which is still well within our 5-minute
  // cleanup threshold.
  const heartbeatId = window.setInterval(() => {
    update(userRef, {
      online: true,
      lastSeen: serverTimestamp(),
    }).catch(() => {
      /* swallow — next tick retries */
    })
  }, HEARTBEAT_INTERVAL_MS)

  return () => {
    window.clearInterval(heartbeatId)
    unsubConnected()
    // Best-effort explicit cleanup for the sign-out / route-away
    // path. RTDB's onDisconnect still runs server-side if this
    // throws (e.g., offline) — belt and suspenders.
    remove(userRef).catch(() => {
      /* swallow; onDisconnect will handle it */
    })
  }
}

/// Update which level the user is currently playing. Passing `null`
/// (e.g., the GameView is unmounting back to the home screen) drops
/// the `levelId` field while preserving the rest of the entry — the
/// user is still online, just not in a level. RTDB's `update` is a
/// shallow patch, so we don't disturb the active `onDisconnect →
/// remove` cleanup.
///
/// We include `online: true` in every update — not just the initial
/// write — to cover a race where GameView mounts before
/// `startPresence`'s onConnected callback has written the base
/// entry. Without `online`, the create-via-update would fail the
/// `.validate` rule (which requires `online` + `lastSeen` children).
/// In the steady-state case the field is already true and writing
/// it again is a no-op.
export function setActivePresenceLevel(
  uid: string,
  levelId: string | null,
): void {
  const userRef = ref(firebaseDatabase, `${PRESENCE_PATH}/${uid}`)
  // RTDB's `update` interprets a literal `null` value as a delete
  // for that field — which is exactly the semantic we want when
  // clearing levelId.
  update(userRef, {
    online: true,
    lastSeen: serverTimestamp(),
    levelId: levelId ?? null,
  }).catch((err) => {
    console.warn('presence level update failed', err)
  })
}

/// Live count of online users. Calls back with the current number
/// whenever the presence node changes. Returns an unsubscribe
/// function. Retained for callers that only need the total — the
/// breakdown subscriber below covers everyone who needs per-level.
export function subscribePlayingNowCount(
  callback: (count: number) => void,
): () => void {
  const presenceRef = ref(firebaseDatabase, PRESENCE_PATH)
  return onValue(presenceRef, (snap) => {
    // `snap.size` is the number of children — exactly the count of
    // online users since `startPresence` writes one entry per uid
    // and `onDisconnect` removes it.
    callback(snap.size)
  })
}

export interface PlayingNowBreakdown {
  total: number
  /// Per-level counts. Levels with zero players are omitted. The
  /// total above counts every online user, including those without
  /// a `levelId` (sitting on the home screen).
  byLevel: Record<string, number>
}

/// Subscribe to the playing-now total + per-level breakdown. Iterates
/// the same presence snapshot as `subscribePlayingNowCount` but
/// additionally buckets by `levelId` for HomeScreen's inline
/// breakdown and the per-pin badges. Single-shot client-side
/// aggregation is fine at our concurrent-user scale — see the design
/// notes in the PR introducing this for the cross-over point where a
/// `byLevel/{id}/{uid}` index would be cheaper.
export function subscribePlayingNowBreakdown(
  callback: (breakdown: PlayingNowBreakdown) => void,
): () => void {
  const presenceRef = ref(firebaseDatabase, PRESENCE_PATH)
  return onValue(presenceRef, (snap) => {
    const byLevel: Record<string, number> = {}
    snap.forEach((child) => {
      const lvl = child.child('levelId').val()
      if (typeof lvl === 'string' && lvl.length > 0) {
        byLevel[lvl] = (byLevel[lvl] ?? 0) + 1
      }
      // forEach returns false to continue (RTDB convention).
      return undefined
    })
    callback({ total: snap.size, byLevel })
  })
}
